import * as React from 'react';
import styled from 'styled-components';

import { isPermitted } from 'util/PermissionsMixin';
import useHistory from 'routing/useHistory';
import Routes from 'routing/Routes';
import Modal from 'components/bootstrap/Modal';
import BootstrapModalConfirm from 'components/bootstrap/BootstrapModalConfirm';
import { useAddEvidence, useRemoveEvidence } from 'security-app/hooks/useInvestigationsAPI';
import type { InvestigationsDetailsAPIType, SearchEvidenceType } from 'security-app/hooks/api/investigationsAPI.types';
import useCurrentUser from 'hooks/useCurrentUser';
import { Button, Label, Table } from 'components/bootstrap';
import { Icon } from 'components/common';
import { DataWell } from 'common/components';
import { TD, TH, RowTitle } from 'security-app/components/common';

import SavedSearchesOverview from './SavedSearchesOverview';

import { IconButton } from '../styled.components';

const StyledCollapsableSectionHeading = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const StyledLabel = styled(Label)`
  display: flex;
  flex-direction: row;
  font-size: 14px;
  margin-left: 0.5rem;
`;

type Props = {
  investigation: InvestigationsDetailsAPIType;
  isExpanded: boolean;
  onToggleExpanded: (sectionName: string) => void;
};

function Searches({ investigation, isExpanded, onToggleExpanded }: Props) {
  const { permissions } = useCurrentUser();
  const [showAddModal, setShowAddModal] = React.useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState<boolean>(false);
  const [deleteSearchId, setDeleteSearchId] = React.useState<string>(null);
  const history = useHistory();

  const canManageEvidence = React.useMemo(
    () => !investigation.archived && isPermitted(permissions, 'investigations:edit'),
    [investigation.archived, permissions],
  );

  const { addEvidence } = useAddEvidence();
  const { removeEvidence, removingEvidence } = useRemoveEvidence();

  const onAddSearch = async (id: string) => {
    await addEvidence({ invId: investigation.id, payload: { searches: [id] } }).then(() => {
      setShowAddModal(false);
    });
  };

  const removeSearch = async () => {
    await removeEvidence({ invId: investigation.id, payload: { searches: [deleteSearchId] } });
    setShowDeleteDialog(false);
    setDeleteSearchId(null);
  };

  const onDelete = (searchId: string) => () => {
    setShowDeleteDialog(true);
    setDeleteSearchId(searchId);
  };

  const onCancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteSearchId(null);
  };

  const renderSearches = (searches) => (
    <>
      {searches.map((search: SearchEvidenceType) => (
        <tr key={search.id}>
          <TD data-testid="search-name">
            <RowTitle onClick={() => history.push(Routes.VIEWS.VIEWID(search.id))}>{search.title}</RowTitle>
          </TD>
          <TD>{search.type}</TD>
          {canManageEvidence && (
            <TD>
              <IconButton $danger data-testid={`delete-${search.id}`} onClick={onDelete(search.id)}>
                <Icon name="close" />
              </IconButton>
            </TD>
          )}
        </tr>
      ))}
    </>
  );

  return (
    <DataWell $color="content">
      <StyledCollapsableSectionHeading>
        <IconButton data-testid="toggle-searches" onClick={() => onToggleExpanded('searches')}>
          {isExpanded ? <Icon name="keyboard_arrow_up" /> : <Icon name="keyboard_arrow_down" />}
          <h2 style={{ marginLeft: '1rem' }}>Searches</h2>
          <StyledLabel>{investigation.searches.length}</StyledLabel>
        </IconButton>
        {canManageEvidence && (
          <Button data-testid="add-search" onClick={() => setShowAddModal(true)}>
            <Icon name="add" size="sm" />
            Add Searches
          </Button>
        )}
      </StyledCollapsableSectionHeading>
      {isExpanded && (
        <div style={{ marginTop: '0.5rem' }}>
          {investigation.searches.length === 0 ? (
            <div style={{ color: 'gray' }}>There are no searches to list</div>
          ) : (
            <Table condensed hover className="table table-striped" data-testid="logs-list">
              <thead>
                <tr>
                  <TH>Search Name</TH>
                  <TH>Search Type</TH>
                  <TH />
                </tr>
              </thead>
              <tbody>{renderSearches(investigation.searches)}</tbody>
            </Table>
          )}
        </div>
      )}
      <Modal show={showAddModal} bsSize="large" onHide={() => setShowAddModal(false)}>
        <Modal.Header>
          <Modal.Title>Add Search</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SavedSearchesOverview onAddSearch={onAddSearch} />
        </Modal.Body>
      </Modal>
      <BootstrapModalConfirm
        showModal={showDeleteDialog}
        title="Delete Search"
        onConfirm={removeSearch}
        onCancel={onCancelDelete}
        confirmButtonDisabled={removingEvidence}>
        Are you sure you want to delete this Search?
      </BootstrapModalConfirm>
    </DataWell>
  );
}

export default Searches;
